import {
  faArrowDown,
  faArrowUp,
  faArrowsRotate,
  faUserPlus,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { AnimatePresence, motion } from 'framer-motion'
import type React from 'react'
import type {
  DashboardActivity,
  DashboardActivityKind,
} from '../../../redux/services/coreApi'
import CountryFlag from './CountryFlag'
import {
  countryAlpha2,
  countryLabel,
  formatMoneyCompact,
  relativeTime,
} from './dashboardFormat'

interface ActivityFeedProps {
  items: DashboardActivity[]
  /** Display currency for the per-event amount (e.g. "DKK", "EUR"). */
  currency: string
}

// FontAwesome glyphs (not emoji): the Raspberry Pi wall board has no colour-emoji font, so emoji icons
// render as monochrome boxes. SVG icons render identically everywhere.
const KIND_META: Record<
  DashboardActivityKind,
  { icon: typeof faArrowsRotate; label: string; ring: string; iconColor: string }
> = {
  swap: {
    icon: faArrowsRotate,
    label: 'Trade executed',
    ring: 'bg-amber-400/15',
    iconColor: 'text-amber-300',
  },
  deposit: {
    icon: faArrowDown,
    label: 'Deposit received',
    ring: 'bg-emerald-400/15',
    iconColor: 'text-emerald-300',
  },
  withdraw: {
    icon: faArrowUp,
    label: 'Withdrawal sent',
    ring: 'bg-sky-400/15',
    iconColor: 'text-sky-300',
  },
  new_customer: {
    icon: faUserPlus,
    label: 'New customer onboarded',
    ring: 'bg-fuchsia-400/15',
    iconColor: 'text-fuchsia-300',
  },
}

// A stable identity for an event so the list animates only genuinely new rows on each poll rather than
// re-flashing everything. The feed has no ids (events are anonymised aggregates), so we derive one.
function activityKey(item: DashboardActivity): string {
  return [
    item.kind,
    item.occurredAtUtc,
    item.amount ?? '',
    item.assetIsoCode ?? '',
    item.country ?? '',
  ].join('|')
}

const ActivityRow: React.FC<{ item: DashboardActivity; currency: string }> = ({
  item,
  currency,
}) => {
  const meta = KIND_META[item.kind]
  const hasFlag = countryAlpha2(item.country) != null

  const detailParts: string[] = []
  if (item.amount != null)
    detailParts.push(formatMoneyCompact(item.amount, currency))
  if (item.assetIsoCode) detailParts.push(item.assetIsoCode)
  if (item.country) detailParts.push(countryLabel(item.country))

  return (
    <motion.li
      layout
      initial={{ opacity: 0, y: -12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.35 }}
      className="flex items-center gap-3 rounded-xl bg-white/[0.03] px-3 py-2.5"
    >
      <span
        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-base ${meta.ring}`}
        aria-hidden
      >
        <FontAwesomeIcon icon={meta.icon} className={meta.iconColor} />
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-white/90 xl:text-base">
          {meta.label}
        </p>
        <p className="truncate text-xs text-white/45 xl:text-sm">
          {detailParts.join(' · ') || '—'}
        </p>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {hasFlag && <CountryFlag country={item.country} className="h-4 w-6" />}
        <span className="whitespace-nowrap text-xs text-white/35">
          {relativeTime(item.occurredAtUtc)}
        </span>
      </div>
    </motion.li>
  )
}

const ActivityFeed: React.FC<ActivityFeedProps> = ({ items, currency }) => (
  <div className="flex h-full min-h-0 flex-col">
    <div className="mb-3 flex shrink-0 items-center gap-2">
      <span className="relative flex h-2.5 w-2.5">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
        <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-400" />
      </span>
      <h3 className="text-xs font-semibold uppercase tracking-widest text-white/40">
        Live activity
      </h3>
    </div>
    {items.length === 0 ? (
      <p className="text-sm text-white/30">Waiting for the next event…</p>
    ) : (
      <ul className="flex min-h-0 flex-1 flex-col gap-2 overflow-hidden">
        <AnimatePresence initial={false}>
          {items.map(item => (
            <ActivityRow
              key={activityKey(item)}
              item={item}
              currency={currency}
            />
          ))}
        </AnimatePresence>
      </ul>
    )}
  </div>
)

export default ActivityFeed
